import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Check, Keyboard, Loader2 } from 'lucide-react';
import { autoPunctuate } from './VoiceQuestion';
import { configureEnglishRecognition, getSpeechRecognitionConstructor, getSttAvailability, getSttErrorMessage } from '../utils/speechRecognition';

/**
 * ExplanationVoiceInput
 *
 * Compact voice-first input used for Phase 4's "Why This Thematic Domain Selected"
 * textarea. Mirrors the Phase 1 VoiceQuestion UX:
 *   • Start / Stop mic (Web Speech STT)
 *   • Live interim transcription
 *   • Auto-punctuation on stop
 *   • "Confirm & Save" button that commits the text via `onSave`
 *   • "Type instead" toggle that hides the mic and shows a plain textarea
 */
const ExplanationVoiceInput = ({ i, wIdx, value, onSave, disabled }) => {
  const [typeMode, setTypeMode] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);
  const interimRef = useRef(value || '');

  useEffect(() => {
    // Keep local draft in sync when the persisted value changes from outside.
    setDraft(value || '');
    interimRef.current = value || '';
  }, [value]);

  const startingRef = useRef(false);
  const availability = getSttAvailability();
  const supported = availability.available;

  const start = () => {
    if (!supported) { setError(availability.message); return; }
    if (isRecording || startingRef.current || recognitionRef.current || disabled) return;
    startingRef.current = true;
    try {
      const SR = getSpeechRecognitionConstructor();
      const rec = new SR();
      configureEnglishRecognition(rec);
      const seedAtStart = (draft || '').trim();
      rec.onstart = () => { setIsRecording(true); setError(''); };
      rec.onresult = (evt) => {
        const finals = [];
        let interim = '';
        for (let k = 0; k < evt.results.length; k++) {
          const chunk = (evt.results[k][0]?.transcript || '').trim();
          if (!chunk) continue;
          if (evt.results[k].isFinal) {
            const last = finals[finals.length - 1];
            if (!last || last.toLowerCase() !== chunk.toLowerCase()) finals.push(chunk);
          } else {
            interim += (interim ? ' ' : '') + chunk;
          }
        }
        const seed = seedAtStart;
        const dictated = [finals.join(' '), interim].filter(Boolean).join(' ').trim();
        const merged = [seed, dictated].filter(Boolean).join(' ');
        interimRef.current = merged;
        setDraft(merged);
      };
      rec.onerror = (e) => { setError(getSttErrorMessage(e?.error)); recognitionRef.current = null; startingRef.current = false; setIsRecording(false); };
      rec.onend = () => {
        // Auto-punctuate the dictated body only (leave any pre-existing typed text intact).
        const seed = seedAtStart;
        const raw = interimRef.current || '';
        const dictated = seed ? raw.slice(seed.length).trim() : raw.trim();
        const finalText = seed
          ? `${seed} ${dictated ? autoPunctuate(dictated) : ''}`.trim()
          : autoPunctuate(dictated);
        setDraft(finalText);
        interimRef.current = finalText;
        setIsRecording(false);
        recognitionRef.current = null;
        startingRef.current = false;
      };
      recognitionRef.current = rec;
      rec.start();
    } catch (_) {
      recognitionRef.current = null;
      startingRef.current = false;
      setError(getSttErrorMessage());
      setIsRecording(false);
    }
  };

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch (_) { /* ignore */ }
    recognitionRef.current = null;
    startingRef.current = false;
    setIsRecording(false);
  };

  useEffect(() => () => stop(), []);

  const confirm = () => {
    // Auto-punctuate anything the user typed manually too, then commit.
    const cleaned = autoPunctuate(draft);
    setDraft(cleaned);
    onSave(cleaned);
  };

  return (
    <div className="mt-2" data-testid={`explain-voice-${i}-${wIdx}`}>
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <label className="block text-xs font-semibold text-gray-700">
          Why This Thematic Domain Selected :
        </label>
        <div className="flex items-center gap-1">
          {!disabled && !typeMode && (
            <button
              type="button"
              onClick={() => (isRecording ? stop() : start())}
              disabled={disabled}
              className={`inline-flex items-center gap-1 text-[11px] rounded border px-2 py-1 ${
                isRecording
                  ? 'bg-red-50 border-red-300 text-red-700 animate-pulse'
                  : 'bg-white border-emerald-300 text-emerald-800 hover:bg-emerald-50'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid={`mic-explain-${i}-${wIdx}`}
              title={!supported ? 'Speech recognition not supported' : (isRecording ? 'Stop recording' : 'Dictate answer')}
            >
              {isRecording ? <MicOff className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
              {isRecording ? 'Stop' : 'Speak'}
            </button>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={() => setTypeMode((v) => !v)}
              className="inline-flex items-center gap-1 text-[11px] rounded border border-gray-300 text-gray-700 hover:bg-gray-50 px-2 py-1"
              data-testid={`btn-type-instead-explain-${i}-${wIdx}`}
              title={typeMode ? 'Switch back to voice input' : 'Switch to plain typing'}
            >
              <Keyboard className="w-3 h-3" />
              {typeMode ? 'Voice instead' : 'Type instead'}
            </button>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={confirm}
              disabled={!draft || draft === value}
              className="inline-flex items-center gap-1 text-[11px] rounded bg-green-600 text-white hover:bg-green-700 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`btn-confirm-explain-${i}-${wIdx}`}
              title="Save this explanation"
            >
              <Check className="w-3 h-3" /> Confirm
            </button>
          )}
        </div>
      </div>
      <textarea
        placeholder={isRecording ? 'Listening… speak now' : 'Why this response fits this category…'}
        className={`w-full text-sm p-2 border rounded focus:outline-none focus:ring-2 resize-none ${
          isRecording ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
        }`}
        rows="3"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={disabled}
        data-testid={`explain-textarea-${i}-${wIdx}`}
      />
      {isRecording && (
        <div className="mt-1 text-[11px] text-red-700 italic inline-flex items-center gap-1" data-testid={`explain-listening-${i}-${wIdx}`}>
          <Loader2 className="w-3 h-3 animate-spin" /> Listening… press Stop when done, then Confirm.
        </div>
      )}
      {error && <div className="mt-1 text-[11px] text-red-600">{error}</div>}
    </div>
  );
};

export default ExplanationVoiceInput;
